import { Component } from 'preact';
import { motion, AnimatePresence } from 'framer-motion';
import { ContextMenuContent, ContextMenuItem, ContextMenuSeparator } from './ui/context-menu';
import {
  GOLD, GOLD_TEXT, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, PANEL_BG, ACCENT_GOLD,
  SECTION_HEADER_STYLE, OrnamentalDivider,
} from '../lib/medievalTheme';
import { Sparkles, Rainbow, Image as ImageIcon } from 'lucide-react';
import { t, k } from '../localization/t';
import { getImageUrl } from './DeckCardTile';
import {
  ART_GROUP_BASE, artGroupOf, artGroupDisplayName,
} from '../utils/sorcery/artGroups';
import { playUI, UI } from '../utils/arena/uiSounds';
import { resolveAvatarRules } from '../utils/game/avatarRules';
import { isPeasantLegal } from '../utils/sorcery/deckRules';
import { isFreePlay } from '../utils/freePlay';
import {
  CARD_SORT_BY_COST, CARD_SORT_OPTIONS, CARD_SORT_PREF_KEY, CARD_SORT_DIR_PREF_KEY,
  CARD_SORT_DIR_ASC, CARD_SORT_DIR_DESC,
  normalizeCardSort, normalizeCardSortDir,
} from '../utils/sorcery/cardSort';
import { getPref, setPref } from '../utils/persistentPrefs';
import { InspectorHoldTracker } from '../utils/inspectorHold';

const SHOW_ART_PREF_KEY = 'vk-deck-sidebar-art';

// Sampled once at module load. An OS-level setting; Electron restarts on change.
const REDUCED_MOTION = typeof window !== 'undefined'
  && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Row enter/exit/reorder motion. Enter slides in from the badge side; exit is
// a fast fade (popLayout lifts the row out of flow so siblings close the gap
// via their layout animation). Reduced motion keeps the fades, drops movement.
const ROW_MOTION = {
  layout: REDUCED_MOTION ? false : 'position',
  initial: REDUCED_MOTION ? { opacity: 0 } : { opacity: 0, x: -8 },
  animate: REDUCED_MOTION ? { opacity: 1 } : { opacity: 1, x: 0 },
  exit: { opacity: 0, transition: { duration: 0.1 } },
  transition: { duration: 0.16, ease: [0.22, 1, 0.36, 1] },
};

const ELEMENT_COLORS = {
  Water: '#01FFFF',
  Earth: '#CFA572',
  Fire: '#FF5F00',
  Air: '#A0BADB',
};

function ElementPip({ element }) {
  // Sorcery convention: Water/Earth point DOWN, Fire/Air point UP. The
  // horizontal bar on Earth (downward) and Air (upward) sits in the wide
  // portion of the triangle. Near the top for Earth, near the bottom
  // for Air. Matching the SorceryElementIcon used in DeckEditorCollection.
  const isUp = element === 'Fire' || element === 'Air';
  const color = ELEMENT_COLORS[element] || '#888';
  const points = isUp ? '5,1 9,9 1,9' : '5,9 1,1 9,1';
  const line = element === 'Earth'
    ? { x1: 2, y1: 4, x2: 8, y2: 4 }
    : element === 'Air'
      ? { x1: 2, y1: 6, x2: 8, y2: 6 }
      : null;

  return (
    <svg viewBox="0 0 10 10" style={{ width: '0.625cqw', height: '0.625cqw', flexShrink: 0 }}>
      <polygon points={points} fill="none" stroke={color} strokeWidth="1.2" />
      {line && <line {...line} stroke={color} strokeWidth="1.2" />}
    </svg>
  );
}

function getThresholdPips(card) {
  const pips = [];
  const thresholds = [
    ['Water', card.waterThreshold],
    ['Earth', card.earthThreshold],
    ['Fire', card.fireThreshold],
    ['Air', card.airThreshold],
  ];
  for (const [element, count] of thresholds) {
    for (let i = 0; i < (count || 0); i++) {
      pips.push({ element, key: `${element}-${i}` });
    }
  }
  return pips;
}

const TYPE_ORDER = { Avatar: 0, Minion: 1, Magic: 2, Aura: 3, Artifact: 4, Site: 5 };

function typeRank(card) {
  const t = card?.type;
  return TYPE_ORDER[t] ?? 99;
}

// Same null-vs-finite shape as cardSort.js's costValue. Normalised
// cards store cost as a string ("3" or "") via statToString, so this
// coerces. Returning null for cost-less cards lets the comparator pin
// them to the end of each type bucket without flipping when direction
// toggles.
function costValue(card) {
  const c = card?.cost;
  if (c === null || c === undefined || c === '') return null;
  const n = typeof c === 'number' ? c : Number(c);
  return Number.isFinite(n) ? n : null;
}

// Rows aggregate by (card, art group, foiling). The same identity the
// collection is stored under. Two arts of one card are separate rows, so a
// deck running two booster Apprentice Wizards and two Welcome Kit ones reads
// as two rows of two rather than one row of four.
export function aggregateAndSort(entries, sortBy, sortDir) {
  const map = new Map();
  for (const entry of entries) {
    const foiling = entry.printing?.foiling || 'S';
    const artGroup = artGroupOf(entry.printing);
    const key = `${entry.card.unique_id}::${artGroup}::${foiling}`;
    if (map.has(key)) {
      map.get(key).qty += 1;
    } else {
      map.set(key, { card: entry.card, printing: entry.printing, artGroup, foiling, qty: 1 });
    }
  }
  const useCost = sortBy === CARD_SORT_BY_COST;
  // Direction only applies to Cost. Name within a type bucket is always
  // ascending so the deck list reads top-to-bottom in a predictable way.
  const sign = useCost && sortDir === CARD_SORT_DIR_DESC ? -1 : 1;
  return Array.from(map.values()).sort((a, b) => {
    // Type grouping always comes first so the visual sub-headers (Minions,
    // Magics, …) stay intact regardless of the inner sort mode.
    const typeCmp = typeRank(a.card) - typeRank(b.card);
    if (typeCmp !== 0) return typeCmp;
    if (useCost) {
      const va = costValue(a.card);
      const vb = costValue(b.card);
      if (va === null && vb !== null) return 1;
      if (vb === null && va !== null) return -1;
      if (va !== null && vb !== null && va !== vb) return sign * (va - vb);
    }
    const nameCmp = (a.card.name || '').localeCompare(b.card.name || '');
    if (nameCmp !== 0) return nameCmp;
    // Base art before alt arts, then standard before foil before rainbow.
    const groupCmp = (a.artGroup === ART_GROUP_BASE ? 0 : 1) - (b.artGroup === ART_GROUP_BASE ? 0 : 1)
      || a.artGroup.localeCompare(b.artGroup);
    if (groupCmp !== 0) return groupCmp;
    const foilOrder = { S: 0, F: 1, R: 2 };
    return (foilOrder[a.foiling] || 0) - (foilOrder[b.foiling] || 0);
  });
}

function isFoilCode(foiling) {
  return foiling === 'F' || foiling === 'R';
}

// A handful of catalog names carry a parenthetical variant suffix
// ("Frog (Blue)", "Foot Soldier (English)"). The deck list shows the plain
// name; the row's hover title still carries the full one.
export function stripNameSuffix(name) {
  return String(name || '').replace(/\s*\([^()]*\)\s*$/, '');
}

function FoilIcon({ foiling }) {
  if (foiling === 'R') {
    return <Rainbow className="shrink-0" style={{ width: "0.6875cqw", height: "0.6875cqw", color: '#c480e0' }} />;
  }
  return <Sparkles className="shrink-0" style={{ width: "0.6875cqw", height: "0.6875cqw", color: ACCENT_GOLD }} />;
}

export function canMoveToZone(sourceZone, targetZone, isSite) {
  if (sourceZone === targetZone) return false;
  // Avatar zone is locked. Can't drag to/from it
  if (sourceZone === 'avatar' || targetZone === 'avatar') return false;
  // Only sites can go to atlas
  if (!isSite && targetZone === 'atlas') return false;
  // Sites cannot go to spellbook
  if (isSite && targetZone === 'spellbook') return false;
  return true;
}

export const ZONES = [
  { key: 'avatar', label: k('Avatar') },
  { key: 'spellbook', label: k('Spellbook') },
  { key: 'atlas', label: k('Atlas') },
  { key: 'collection', label: k('Collection') },
];

const TYPE_LABELS = {
  Avatar: 'Avatars',
  Minion: 'Minions',
  Magic: 'Magics',
  Aura: 'Auras',
  Artifact: 'Artifacts',
  Site: 'Sites',
};

function groupRowsByType(rows) {
  const buckets = new Map();
  for (const row of rows) {
    const type = TYPE_LABELS[row.card?.type] ? row.card.type : 'Other';
    if (!buckets.has(type)) buckets.set(type, []);
    buckets.get(type).push(row);
  }
  return Array.from(buckets, ([type, items]) => ({ type, rows: items }));
}

export default class DeckEditorSidebar extends Component {
  constructor(props) {
    super(props);
    this.state = {
      collapsed: { avatar: false, spellbook: false, atlas: false, collection: false },
      collapsedTypes: new Set(),
      hoveredHeader: null,
      pressedHeader: null,
      contextMenu: null,
      // hoveredRow lives in an instance field (_hoveredRow), NOT state: it is
      // only read by the keydown handler and never in render, so storing it in
      // state would re-render the whole row list on every hover (the fast-scroll
      // jank). The warning tooltips below DO render, so they stay in state.
      // Snapshot for the peasant-illegal hover tooltip. Captured at
      // mouseenter time (rect + rarity + name) and cleared on leave,
      // so the floating tooltip can be rendered in a fixed-position
      // overlay without re-querying the DOM.
      illegalHover: null, // { rect, rarity, name } | null
      // Same shape, separate state slot. The rule-invalid tooltip uses
      // a red theme and Imposter-specific copy, so keeping them apart
      // simplifies the rendering branches over a discriminated union.
      ruleInvalidHover: null, // { rect, name } | null
      // Hover snapshot for unowned cards in ranked-eligible modes
      // (constructed / peasant). Separate slot so the tooltip can
      // carry mode-aware copy and the owned / need counts without
      // overloading the format-illegal state.
      unownedHover: null, // { rect, name, owned, need } | null
      dragCardId: null,
      dragCardName: '',
      dragSourceZone: null,
      dragIsSite: false,
      dragOverZone: null,
      sortBy: normalizeCardSort(getPref(CARD_SORT_PREF_KEY, 'name')),
      sortDir: normalizeCardSortDir(getPref(CARD_SORT_DIR_PREF_KEY, CARD_SORT_DIR_ASC)),
      showArt: getPref(SHOW_ART_PREF_KEY, false) === true,
    };
  }

  toggleArt = () => {
    const next = !this.state.showArt;
    playUI(UI.HOVER, { volume: 0.5 });
    setPref(SHOW_ART_PREF_KEY, next);
    this.setState({ showArt: next });
  };

  handleSortByChange = (value) => {
    const next = normalizeCardSort(value);
    playUI(UI.HOVER, { volume: 0.5 });
    // Name never toggles direction. It's always A→Z. Cost flips between
    // ascending and descending each time the chip is re-clicked.
    if (next !== CARD_SORT_BY_COST) {
      if (next === this.state.sortBy && this.state.sortDir === CARD_SORT_DIR_ASC) return;
      setPref(CARD_SORT_PREF_KEY, next);
      setPref(CARD_SORT_DIR_PREF_KEY, CARD_SORT_DIR_ASC);
      this.setState({ sortBy: next, sortDir: CARD_SORT_DIR_ASC });
      return;
    }
    if (next === this.state.sortBy) {
      const nextDir = this.state.sortDir === CARD_SORT_DIR_ASC
        ? CARD_SORT_DIR_DESC
        : CARD_SORT_DIR_ASC;
      setPref(CARD_SORT_DIR_PREF_KEY, nextDir);
      this.setState({ sortDir: nextDir });
      return;
    }
    setPref(CARD_SORT_PREF_KEY, next);
    setPref(CARD_SORT_DIR_PREF_KEY, CARD_SORT_DIR_ASC);
    this.setState({ sortBy: next, sortDir: CARD_SORT_DIR_ASC });
  };

  // Tap-vs-hold state for the Space inspector hotkey. Same tracker the
  // collection grid uses, so both surfaces behave identically: tap to
  // pin the inspector open, hold to peek and release to close.
  inspectorHold = new InspectorHoldTracker();

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
  }

  handleKeyDown = (e) => {
    const tag = e.target?.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
    const hoveredRow = this._hoveredRow;

    // Space → inspect the hovered row. Only wired on surfaces that pass
    // onInspect (the deck editor); elsewhere Space stays untouched.
    if (this.props.onInspect && (e.key === ' ' || e.code === 'Space')) {
      if (this.props.inspectorOpen) {
        e.preventDefault();
        if (e.repeat) return;
        this.inspectorHold.noteClose();
        this.props.onInspectClose?.();
        return;
      }
      // No row hovered. Leave the press to the collection grid, which
      // arms its own pending-open.
      if (!hoveredRow) return;
      e.preventDefault();
      // OS key-repeat fires while Space is held; only the first keydown acts.
      if (e.repeat) return;
      this.inspectorHold.noteOpen();
      this.props.onInspect(hoveredRow.cardId, hoveredRow.artGroup, hoveredRow.foiling);
      return;
    }

    if (!hoveredRow) return;
    // The inspector covers the row the zone hotkeys would act on, so a
    // stale hover must not silently move a card while it's open.
    if (this.props.inspectorOpen) return;

    const { cardId, artGroup, foiling, zone, isSite } = hoveredRow;
    const key = e.key.toLowerCase();

    // Source zone has to flow through to the parent's findIndex match.
    // without it, a second C-press on a different copy of the same card
    // re-finds the first entry (now in collection) and the move silently
    // no-ops. Same hazard for the S/A hotkeys whenever there are multiple
    // copies straddling zones.
    if (key === 's' && !isSite && this.canMoveToZone(zone, 'spellbook', isSite)) {
      e.preventDefault();
      this.props.onChangeZone?.(cardId, 'spellbook', artGroup, foiling, zone);
    } else if (key === 'a' && isSite && this.canMoveToZone(zone, 'atlas', isSite)) {
      e.preventDefault();
      this.props.onChangeZone?.(cardId, 'atlas', artGroup, foiling, zone);
    } else if (key === 'c' && this.canMoveToZone(zone, 'collection', isSite)) {
      e.preventDefault();
      this.props.onChangeZone?.(cardId, 'collection', artGroup, foiling, zone);
    }
  };

  // Releasing Space closes the inspector only when it was held past the
  // hold threshold; a quick tap leaves it pinned open.
  handleKeyUp = (e) => {
    if (!this.props.onInspect) return;
    if (e.key !== ' ' && e.code !== 'Space') return;
    if (!this.props.inspectorOpen) return;
    if (!this.inspectorHold.shouldCloseOnKeyUp()) return;
    e.preventDefault();
    this.props.onInspectClose?.();
  };

  toggleSection = (zone) => {
    this.setState((prev) => ({
      collapsed: { ...prev.collapsed, [zone]: !prev.collapsed[zone] },
    }));
  };

  setHeaderHover = (id, hovered) => {
    this.setState((prev) => {
      if (hovered) return { hoveredHeader: id };
      if (prev.hoveredHeader === id) return { hoveredHeader: null };
      return null;
    });
  };

  setHeaderPressed = (id, pressed) => {
    this.setState((prev) => {
      if (pressed) return { pressedHeader: id };
      if (prev.pressedHeader === id) return { pressedHeader: null };
      return null;
    });
  };

  toggleTypeSection = (zone, type) => {
    this.setState((prev) => {
      const next = new Set(prev.collapsedTypes);
      const key = `${zone}:${type}`;
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { collapsedTypes: next };
    });
  };

  handleRowContext = (e, uniqueId, artGroup, foiling, currentZone, isSite) => {
    e.preventDefault();
    this.setState({
      contextMenu: { x: e.clientX, y: e.clientY, cardId: uniqueId, artGroup, foiling, currentZone, isSite },
    });
  };

  closeContextMenu = () => {
    this.setState({ contextMenu: null });
  };

  handleMoveToZone = (zone) => {
    const { contextMenu } = this.state;
    if (contextMenu) {
      this.props.onChangeZone?.(contextMenu.cardId, zone, contextMenu.artGroup, contextMenu.foiling, contextMenu.currentZone);
    }
    this.closeContextMenu();
  };

  handleRemoveFromMenu = () => {
    const { contextMenu } = this.state;
    if (contextMenu) {
      this.props.onDecrement?.(contextMenu.cardId, contextMenu.artGroup, contextMenu.foiling);
    }
    this.closeContextMenu();
  };

  _hoveredRow = null; // { cardId, artGroup, foiling, zone, isSite }. Instance, not state

  handleRowHover = (uniqueId, artGroup, foiling, element) => {
    this.props.onCardHover?.(uniqueId, artGroup, foiling, element);
  };

  canMoveToZone(sourceZone, targetZone, isSite) {
    return canMoveToZone(sourceZone, targetZone, isSite);
  }

  handleDragStart = (e, cardId, sourceZone, isSite, cardName, artGroup, foiling) => {
    // Native drag-and-drop runs on its own event lane. Children of a
    // draggable parent (the +/− stepper buttons here) put the browser
    // into a "drag-pending" state that swallows hover/cursor events and
    // shows the OS default cursor. Cancelling the drag when its target
    // is inside an opt-out region restores normal pointer behavior on
    // the buttons without losing zone drag-and-drop on the rest of the row.
    if (e.target?.closest?.('[data-no-drag]')) {
      e.preventDefault();
      return;
    }
    playUI(UI.DRAG_START);
    this.setState({
      dragCardId: cardId, dragSourceZone: sourceZone, dragIsSite: isSite,
      dragCardName: cardName, dragArtGroup: artGroup, dragFoiling: foiling,
    });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', cardId);
  };

  handleSectionDragEnter = (targetZone) => {
    const { dragSourceZone, dragIsSite } = this.state;
    if (this.canMoveToZone(dragSourceZone, targetZone, dragIsSite)) {
      this.setState({ dragOverZone: targetZone });
    }
  };

  handleSectionDragLeave = (e, targetZone) => {
    // Only clear if truly leaving the section (not entering a child)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      if (this.state.dragOverZone === targetZone) {
        this.setState({ dragOverZone: null });
      }
    }
  };

  handleDragOver = (e) => {
    const { dragSourceZone, dragIsSite, dragOverZone } = this.state;
    if (dragOverZone && this.canMoveToZone(dragSourceZone, dragOverZone, dragIsSite)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    }
  };

  handleDrop = (e, targetZone) => {
    e.preventDefault();
    const { dragCardId, dragSourceZone, dragIsSite, dragArtGroup, dragFoiling } = this.state;
    if (dragCardId && this.canMoveToZone(dragSourceZone, targetZone, dragIsSite)) {
      this.props.onChangeZone?.(dragCardId, targetZone, dragArtGroup, dragFoiling, dragSourceZone);
    }
    this.setState({
      dragCardId: null, dragSourceZone: null, dragIsSite: false, dragCardName: '',
      dragOverZone: null, dragArtGroup: undefined, dragFoiling: undefined,
    });
  };

  handleDragEnd = () => {
    playUI(UI.DRAG_DROP);
    this.setState({ dragCardId: null, dragSourceZone: null, dragIsSite: false, dragCardName: '', dragOverZone: null });
  };

  renderQtyBadge(row, isUnowned, owned) {
    // Single badge. Left-click adds a copy, right-click removes one.
    // The right-click also has to suppress the row's zone-move context
    // menu so the decrement happens cleanly. data-no-drag opts the
    // badge out of the row's native drag so hover/cursor work cleanly.
    const stop = (e) => { e.stopPropagation(); };
    const onClick = (e) => {
      stop(e);
      this.props.onIncrement?.(row.card.unique_id, row.artGroup, row.foiling);
    };
    const onContextMenu = (e) => {
      e.preventDefault();
      stop(e);
      this.props.onDecrement?.(row.card.unique_id, row.artGroup, row.foiling);
    };

    // Keyed on the quantity so a change remounts the span and replays the
    // pop. Instant feedback for add/remove without touching the row itself.
    const countNode = isUnowned ? (
      <span key={row.qty} className="vh-qty-pop tabular-nums" title={`Own ${owned}, need ${row.qty}`}>
        <span style={{ color: '#c45050' }}>{owned}</span>
        <span style={{ color: TEXT_MUTED }}>/</span>
        <span style={{ color: '#e89090' }}>{row.qty}</span>
      </span>
    ) : (
      <span key={row.qty} className="vh-qty-pop tabular-nums">{row.qty}x</span>
    );

    return (
      <button
        data-no-drag
        type="button"
        aria-label={t('Add one copy. Right-click to remove.')}
        title={t('Click to add. Right-click to remove.')}
        draggable={false}
        onClick={onClick}
        onContextMenu={onContextMenu}
        onMouseDown={stop}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = `${GOLD} 0.22)`;
          e.currentTarget.style.color = '#fff5cc';
          e.currentTarget.style.borderColor = `${GOLD} 0.45)`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = `${GOLD} 0.07)`;
          e.currentTarget.style.color = `${GOLD_TEXT} 0.9)`;
          e.currentTarget.style.borderColor = `${GOLD} 0.22)`;
        }}
        className="relative shrink-0 inline-flex items-center justify-center text-xs font-semibold tabular-nums transition-transform duration-100 active:scale-[0.96]"
        style={{
          minWidth: '2.25cqw',
          height: '1.6cqw',
          padding: '0 0.4cqw',
          borderRadius: 'var(--radius-md)',
          background: `${GOLD} 0.07)`,
          border: `1px solid ${GOLD} 0.22)`,
          color: `${GOLD_TEXT} 0.9)`,
          transition: 'background-color 0.1s ease, color 0.1s ease, border-color 0.1s ease',
        }}
      >
        {countNode}
      </button>
    );
  }

  renderCardRow(row, zoneKey) {
    const isSite = row.card?.type === 'Site' || row.card?.played_horizontally;
    const owned = this.props.ownedMap?.get(row.card.unique_id) || 0;
    const isSandbox = this.props.gameMode === 'sandbox';
    const isPeasantMode = this.props.gameMode === 'peasant';
    // Draft picks are owned the moment they're picked, and the deck
    // gets built from what came out of the packs regardless of format
    // restrictions. So suppress both warnings entirely in draft.
    const isDraft = !!this.props.draftMode;
    const isUnowned = !isSandbox && !isDraft && !isFreePlay() && owned < row.qty;
    const isIllegalInFormat = isPeasantMode && !isDraft && !isPeasantLegal(row.card);
    // A row is rule-invalid when it violates a structural deck rule.
    // currently only the Imposter case: an extra Avatar parked outside
    // the primary slot without Imposter equipped. Save is blocked
    // until the user re-equips Imposter or removes the entry.
    // `invalidEntries` is a Map<unique_id, { kind, message }>; the kind
    // discriminator lets us pick the right tooltip without inferring
    // from card shape. `.has()` is preserved across the Set→Map switch
    // so the boolean tint trigger stays identical.
    const artUrl = this.state.showArt ? getImageUrl(row) : '';
    const isRuleInvalid = !!this.props.invalidEntries?.has(row.card.unique_id);
    const ruleInvalidMessage = this.props.invalidEntries?.get?.(row.card.unique_id)?.message;

    // Shared box model so hover, foil, and unowned highlights all sit
    // on identical geometry. Same height, same corner radius, and a
    // 1px border slot that's transparent in the default state so the
    // content area stays the same width whether or not a state adds a
    // visible border. Without this, foil rows' 1px border shifts the
    // badge ~1px and Tailwind's `rounded` gives default rows a different
    // corner curve from foil rows' inline radius.
    const rowStyleBase = {
      height: '2cqw',
      // Concentric with the quantity badge: badge radius (0.3125cqw) plus
      // the 0.2cqw inset the badge sits at on every side.
      borderRadius: 'var(--radius-lg)',
      border: '1px solid transparent',
      // Gap so adjacent rows' (foil) highlight borders never touch.
      marginBottom: '0.12cqw',
      // Match the badge's vertical inset exactly: vertical gap is the
      // border-box centering of a 1.6cqw badge in a 2cqw row = 0.2cqw to
      // the outer border. Left padding sits inside the 1px border, so
      // subtract 1px to land the badge the same distance from the border.
      paddingLeft: 'calc(0.2cqw - 1px)',
      // Art layer is absolutely positioned inside the row.
      position: 'relative',
      overflow: 'hidden',
      // Named properties only. `transition: all` would drag the hover
      // background repaint and the art layer along with it.
      transitionProperty: 'background-color, border-color, box-shadow',
      transitionDuration: '100ms, 120ms, 120ms',
      transitionTimingFunction: 'ease',
    };
    let rowStyle;
    if (isRuleInvalid) {
      // Deeper red than the unowned tint so a rule violation reads as
      // distinct from "you just don't own enough copies." Both block
      // save, but the rule violation is structural (re-equip Imposter
      // or remove the entry) while unowned can be fixed by acquiring.
      rowStyle = {
        ...rowStyleBase,
        background: 'rgba(200, 50, 50, 0.18)',
        borderColor: 'rgba(220, 80, 80, 0.55)',
        boxShadow: 'inset 0 0 0 1px rgba(220, 80, 80, 0.18)',
      };
    } else if (isIllegalInFormat) {
      // Amber-orange tint so format-illegal cards read as a different
      // class of warning than ownership-red. Same border weight so
      // the row geometry doesn't shift.
      rowStyle = {
        ...rowStyleBase,
        background: 'rgba(220, 140, 40, 0.12)',
        borderColor: 'rgba(220, 140, 40, 0.45)',
        boxShadow: 'inset 0 0 0 1px rgba(220, 140, 40, 0.15)',
      };
    } else if (isUnowned) {
      rowStyle = {
        ...rowStyleBase,
        background: 'rgba(180, 50, 50, 0.08)',
        borderColor: 'rgba(180, 50, 50, 0.2)',
      };
    } else if (isFoilCode(row.foiling)) {
      rowStyle = {
        ...rowStyleBase,
        background: row.foiling === 'R'
          ? 'linear-gradient(135deg, rgba(217,142,255,0.06) 0%, rgba(255,154,158,0.04) 50%, rgba(109,213,237,0.06) 100%)'
          : 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 100%)',
        borderColor: row.foiling === 'R' ? 'rgba(217,142,255,0.2)' : 'rgba(255,255,255,0.2)',
        boxShadow: `inset 0 1px 0 ${row.foiling === 'R' ? 'rgba(217,142,255,0.08)' : 'rgba(255,255,255,0.08)'}, inset 0 -1px 0 rgba(0,0,0,0.15)`,
      };
    } else {
      rowStyle = { ...rowStyleBase, background: 'transparent' };
    }

    // Hover stroke. Warning tints keep their own border so the colour still
    // codes the problem. Foils get the card inspector's foil recipe. A white
    // outer stroke plus a directional inset sheen, lit from the top-left and
    // faint at the bottom-right so the edge reads as worn plating rather than
    // a flat line. Plain rows only need a stroke once artwork is behind them;
    // without art the background wash alone is legible.
    const hasWarningTint = isRuleInvalid || isIllegalInFormat || isUnowned;
    const isFoilRow = !hasWarningTint && isFoilCode(row.foiling);
    let hoverBorderColor;
    if (hasWarningTint) hoverBorderColor = rowStyle.borderColor;
    else if (isFoilRow) hoverBorderColor = 'rgba(255,255,255,0.4)';
    else if (artUrl) hoverBorderColor = `${GOLD} 0.45)`;
    else hoverBorderColor = 'transparent';

    const hoverShadow = [
      rowStyle.boxShadow,
      isFoilRow && 'inset 1px 1px 0 rgba(255,255,255,0.3), inset -1px -1px 0 rgba(255,255,255,0.04)',
      '0 0.15cqw 0.35cqw rgba(0,0,0,0.5)',
    ].filter(Boolean).join(', ');

    return (
      <motion.div
        key={`${row.card.unique_id}::${row.artGroup}::${row.foiling}`}
        {...ROW_MOTION}
        data-sidebar-card-id={row.card.unique_id}
        data-sidebar-art-group={row.artGroup}
        data-sidebar-foiling={row.foiling}
        draggable
        className="flex items-center gap-1.5 pr-2 cursor-grab"
        style={rowStyle}
        onContextMenu={(e) => this.handleRowContext(e, row.card.unique_id, row.artGroup, row.foiling, zoneKey, isSite)}
        onDragStart={(e) => this.handleDragStart(e, row.card.unique_id, zoneKey, isSite, row.card.name, row.artGroup, row.foiling)}
        onDragEnd={this.handleDragEnd}
        onMouseEnter={(e) => {
          playUI(UI.HOVER, { volume: 0.4 });
          // Override hover wash for tinted rows so the warning tint
          // doesn't get repainted to the default gold-on-hover.
          e.currentTarget.style.background = isRuleInvalid
            ? 'rgba(200, 50, 50, 0.26)'
            : isIllegalInFormat
              ? 'rgba(220, 140, 40, 0.18)'
              : isFoilCode(row.foiling)
                // Foils hover to a more opaque version of their own resting
                // tint, not the default amber. Keeps the foil differentiation.
                ? (row.foiling === 'R'
                    ? 'linear-gradient(135deg, rgba(217,142,255,0.12) 0%, rgba(255,154,158,0.08) 50%, rgba(109,213,237,0.12) 100%)'
                    : 'linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.06) 100%)')
                : `${GOLD} 0.06)`;
          // Cast shadow only. The row never moves. Resting box-shadow (foil
          // sheen, warning insets) is layered under the cast so those states
          // survive the hover.
          e.currentTarget.style.boxShadow = hoverShadow;
          e.currentTarget.style.borderColor = hoverBorderColor;
          this.handleRowHover(row.card.unique_id, row.artGroup, row.foiling, e.currentTarget);
          // Instance field, not state: read by the keydown hotkeys, never
          // rendered. So updating it costs nothing and never re-renders the
          // list (the fast-scroll hover path stays cheap).
          this._hoveredRow = { cardId: row.card.unique_id, artGroup: row.artGroup, foiling: row.foiling, zone: zoneKey, isSite };
          // Warning tooltips DO render. Only touch state when this row has a
          // warning or one is currently showing. The common (clean) row makes
          // zero setState calls, so hovering through the list doesn't re-render.
          const hasWarning = isIllegalInFormat || isRuleInvalid || (isUnowned && !isIllegalInFormat && !isRuleInvalid);
          if (hasWarning || this.state.illegalHover || this.state.ruleInvalidHover || this.state.unownedHover) {
            const rowRect = e.currentTarget.getBoundingClientRect();
            this.setState({
              illegalHover: isIllegalInFormat
                ? { rect: rowRect, rarity: row.card.rarity, name: row.card.name }
                : null,
              ruleInvalidHover: isRuleInvalid
                ? {
                    rect: rowRect,
                    name: row.card.name,
                    kind: this.props.invalidEntries?.get?.(row.card.unique_id)?.kind || 'extra_avatar',
                    message: ruleInvalidMessage || 'This card violates a deck rule.',
                  }
                : null,
              // Ownership warning only fires when no higher-priority
              // violation is showing. Peasant illegality and Imposter
              // rule breaks are more actionable so they win the slot.
              unownedHover: (isUnowned && !isIllegalInFormat && !isRuleInvalid)
                ? { rect: rowRect, name: row.card.name, owned, need: row.qty }
                : null,
            });
          }
        }}
        onMouseLeave={(e) => {
          // Restore the row's resting background. Hardcoding 'transparent'
          // here wiped the foil/unowned tints once hovered. RowStyle.background
          // already holds the correct per-row resting value.
          e.currentTarget.style.background = rowStyle.background;
          e.currentTarget.style.boxShadow = rowStyle.boxShadow || 'none';
          e.currentTarget.style.borderColor = rowStyle.borderColor || 'transparent';
          this.props.onCardHover?.(null, null, null);
          this._hoveredRow = null;
          if (this.state.illegalHover || this.state.ruleInvalidHover || this.state.unownedHover) {
            this.setState({ illegalHover: null, ruleInvalidHover: null, unownedHover: null });
          }
        }}
      >
        {/* Card artwork wash. Sits above the row tint, below the content
            (content spans carry `relative` so the positioned art can't
            paint over them). The mask fades the art out toward the left
            so the quantity badge and name stay legible. */}
        {artUrl && (
          <div
            aria-hidden="true"
            className="absolute inset-0 overflow-hidden pointer-events-none"
            style={{
              opacity: 0.55,
              // Mask lives on the un-rotated wrapper so the fade stays
              // horizontal for sites too.
              maskImage: 'linear-gradient(to right, transparent 18%, rgba(0,0,0,0.55) 45%, #000 100%)',
              WebkitMaskImage: 'linear-gradient(to right, transparent 18%, rgba(0,0,0,0.55) 45%, #000 100%)',
            }}
          >
            <div
              className="absolute"
              style={{
                backgroundImage: `url(${artUrl})`,
                backgroundSize: 'cover',
                // Sites ship as portrait images but read horizontally.
                // same 90deg CW flip DeckCardTile applies. A square box
                // is rotation-invariant, so the row keeps its geometry
                // and only the art turns.
                ...(isSite
                  ? {
                      left: 0,
                      top: '50%',
                      width: '100%',
                      aspectRatio: '1',
                      transform: 'translateY(-50%) rotate(90deg)',
                      backgroundPosition: 'center',
                    }
                  : {
                      inset: 0,
                      // Portrait cards keep the art in the upper third.
                      backgroundPosition: 'center 22%',
                    }),
              }}
            />
          </div>
        )}

        {this.renderQtyBadge(row, isUnowned, owned)}

        <span
          className="relative flex-1 min-w-0 flex items-center gap-1"
          title={
            isRuleInvalid
              ? `${row.card.name}. ${ruleInvalidMessage || 'this card violates a deck rule'}`
              : isUnowned
                ? `${row.card.name}. Own ${owned}, need ${row.qty}`
                : row.card.name
          }
        >
          <span
            className="text-xs truncate"
            style={{
              color: isRuleInvalid ? '#ff9090' : isIllegalInFormat ? '#f0b260' : isUnowned ? '#e89090' : TEXT_BODY,
              textShadow: artUrl ? '0 1px 2px rgba(0,0,0,0.9)' : undefined,
            }}
          >
            {stripNameSuffix(artGroupDisplayName(row.card, row.artGroup))}
          </span>
          {isFoilCode(row.foiling) && <FoilIcon foiling={row.foiling} />}
        </span>

        {getThresholdPips(row.card).length > 0 && (
          <span className="relative flex items-center gap-px shrink-0">
            {getThresholdPips(row.card).map((pip) => (
              <ElementPip key={pip.key} element={pip.element} />
            ))}
          </span>
        )}

        {row.card.cost ? (
          <span
            className="relative shrink-0 flex items-center justify-center rounded-full text-xs font-bold"
            style={{
              width: '1.25cqw', height: '1.25cqw',
              // Solid, not a gold wash. The artwork behind the row would
              // otherwise show through and swallow the digit.
              background: '#17120a',
              border: `1px solid ${GOLD} 0.45)`,
              color: ACCENT_GOLD,
            }}
          >
            {row.card.cost}
          </span>
        ) : null}
      </motion.div>
    );
  }


  renderIllegalHoverTooltip() {
    const { illegalHover } = this.state;
    if (!illegalHover?.rect) return null;
    const { rect } = illegalHover;

    // Canvas-local positioning. The .app-canvas wrapper has
    // `transform: translateZ(0)`, which makes it the containing
    // block for position:fixed descendants. So the `left`/`top`
    // we hand the style are measured from the canvas's top-left,
    // not the viewport's. (See CLAUDE.md "UI Scaling Architecture"
    // and SkillTreeScreen's positionTooltip for the same pattern.)
    // The previous version added canvasRect.left/top back to the
    // local coords, which on letterboxed monitors pushed the
    // tooltip off the right side of the canvas.
    const canvasEl = typeof document !== 'undefined' ? document.querySelector('.app-canvas') : null;
    const canvasRect = canvasEl
      ? canvasEl.getBoundingClientRect()
      : { left: 0, top: 0, width: typeof window !== 'undefined' ? window.innerWidth : 1600, height: typeof window !== 'undefined' ? window.innerHeight : 900 };
    const canvasScale = canvasRect.width / 1600;

    const TIP_WIDTH_DESIGN = 200;
    const TIP_MARGIN_DESIGN = 10;
    const TIP_PAD_DESIGN = 12;
    const TIP_HEIGHT_EST_DESIGN = 80;

    const tipWidth = TIP_WIDTH_DESIGN * canvasScale;
    const tipMargin = TIP_MARGIN_DESIGN * canvasScale;
    const tipPad = TIP_PAD_DESIGN * canvasScale;
    const tipHeightEst = TIP_HEIGHT_EST_DESIGN * canvasScale;

    const localTop = rect.top - canvasRect.top;
    const localBottom = rect.bottom - canvasRect.top;
    const localCenterX = rect.left + rect.width / 2 - canvasRect.left;

    // Vertical: prefer below; flip above when below would clip and
    // above has more headroom.
    const spaceBelow = Math.max(0, canvasRect.height - localBottom - tipPad);
    const spaceAbove = Math.max(0, localTop - tipPad);
    const placeAbove = spaceBelow < tipHeightEst + tipMargin && spaceAbove > spaceBelow;
    const localTooltipTop = placeAbove
      ? Math.max(tipPad, localTop - tipHeightEst - tipMargin)
      : localBottom + tipMargin;

    // Horizontal: centered on the row, then clamped inside the
    // canvas so a row at either edge slides the tooltip back into
    // view instead of clipping off the side.
    let localTooltipLeft = localCenterX - tipWidth / 2;
    const maxLeft = canvasRect.width - tipWidth - tipPad;
    if (localTooltipLeft > maxLeft) localTooltipLeft = maxLeft;
    if (localTooltipLeft < tipPad) localTooltipLeft = tipPad;

    return (
      <div
        role="tooltip"
        style={{
          position: 'fixed',
          left: localTooltipLeft,
          top: localTooltipTop,
          width: `${tipWidth}px`,
          pointerEvents: 'none',
          zIndex: 90,
          padding: '0.5cqw 0.75cqw',
          background: 'linear-gradient(180deg, rgba(28,18,8,0.98) 0%, rgba(20,12,4,0.98) 100%)',
          border: '1px solid rgba(220, 140, 40, 0.55)',
          borderRadius: 'var(--radius-md)',
          boxShadow: '0 0.5cqw 1.25cqw rgba(0,0,0,0.55), 0 0 1.25cqw rgba(220,140,40,0.18), inset 0 1px 0 rgba(255, 220, 140, 0.08)',
        }}
      >
        <div
          className="text-[0.625cqw] font-semibold"
          style={{ color: '#f0b260' }}
        >
          Not legal in Peasant
        </div>
        <div
          className="text-[0.6875cqw] mt-1 leading-snug"
          style={{ color: TEXT_MUTED }}
        >
          Only Ordinary and Exceptional cards are allowed.
        </div>
      </div>
    );
  }

  renderRuleInvalidHoverTooltip() {
    const { ruleInvalidHover } = this.state;
    if (!ruleInvalidHover?.rect) return null;
    const { rect, kind, message } = ruleInvalidHover;
    // Title varies by rule violated; message is delivered verbatim from
    // `getInvalidEntries`. Same red palette, same positioning math.
    // only the copy changes between kinds.
    const HEADER_BY_KIND = {
      extra_avatar: 'Invalid extra avatar',
      unpaired_unique: 'Unpaired Unique',
    };
    const headerText = HEADER_BY_KIND[kind] || 'Invalid card';

    // Same canvas-local positioning as the peasant tooltip. See that
    // function for the math notes.
    const canvasEl = typeof document !== 'undefined' ? document.querySelector('.app-canvas') : null;
    const canvasRect = canvasEl
      ? canvasEl.getBoundingClientRect()
      : { left: 0, top: 0, width: typeof window !== 'undefined' ? window.innerWidth : 1600, height: typeof window !== 'undefined' ? window.innerHeight : 900 };
    const canvasScale = canvasRect.width / 1600;

    const tipWidth = 220 * canvasScale;
    const tipMargin = 10 * canvasScale;
    const tipPad = 12 * canvasScale;
    const tipHeightEst = 90 * canvasScale;

    const localTop = rect.top - canvasRect.top;
    const localBottom = rect.bottom - canvasRect.top;
    const localCenterX = rect.left + rect.width / 2 - canvasRect.left;

    const spaceBelow = Math.max(0, canvasRect.height - localBottom - tipPad);
    const spaceAbove = Math.max(0, localTop - tipPad);
    const placeAbove = spaceBelow < tipHeightEst + tipMargin && spaceAbove > spaceBelow;
    const localTooltipTop = placeAbove
      ? Math.max(tipPad, localTop - tipHeightEst - tipMargin)
      : localBottom + tipMargin;

    let localTooltipLeft = localCenterX - tipWidth / 2;
    const maxLeft = canvasRect.width - tipWidth - tipPad;
    if (localTooltipLeft > maxLeft) localTooltipLeft = maxLeft;
    if (localTooltipLeft < tipPad) localTooltipLeft = tipPad;

    return (
      <div
        role="tooltip"
        style={{
          position: 'fixed',
          left: localTooltipLeft,
          top: localTooltipTop,
          width: `${tipWidth}px`,
          pointerEvents: 'none',
          zIndex: 90,
          padding: '0.5cqw 0.75cqw',
          background: 'linear-gradient(180deg, rgba(28,8,8,0.98) 0%, rgba(20,4,4,0.98) 100%)',
          border: '1px solid rgba(220, 80, 80, 0.55)',
          borderRadius: 'var(--radius-md)',
          boxShadow: '0 0.5cqw 1.25cqw rgba(0,0,0,0.55), 0 0 1.25cqw rgba(220,80,80,0.18), inset 0 1px 0 rgba(255, 140, 140, 0.08)',
        }}
      >
        <div
          className="text-[0.625cqw] font-semibold"
          style={{ color: '#ff9090' }}
        >
          {headerText}
        </div>
        <div
          className="text-[0.6875cqw] mt-1 leading-snug"
          style={{ color: TEXT_MUTED }}
        >
          {message}
        </div>
      </div>
    );
  }

  renderUnownedHoverTooltip() {
    const { unownedHover } = this.state;
    if (!unownedHover?.rect) return null;
    const { rect, owned, need } = unownedHover;

    // Same canvas-local positioning as the other row tooltips. See
    // renderIllegalHoverTooltip for the math notes.
    const canvasEl = typeof document !== 'undefined' ? document.querySelector('.app-canvas') : null;
    const canvasRect = canvasEl
      ? canvasEl.getBoundingClientRect()
      : { left: 0, top: 0, width: typeof window !== 'undefined' ? window.innerWidth : 1600, height: typeof window !== 'undefined' ? window.innerHeight : 900 };
    const canvasScale = canvasRect.width / 1600;

    const tipWidth = 220 * canvasScale;
    const tipMargin = 10 * canvasScale;
    const tipPad = 12 * canvasScale;
    const tipHeightEst = 90 * canvasScale;

    const localTop = rect.top - canvasRect.top;
    const localBottom = rect.bottom - canvasRect.top;
    const localCenterX = rect.left + rect.width / 2 - canvasRect.left;

    const spaceBelow = Math.max(0, canvasRect.height - localBottom - tipPad);
    const spaceAbove = Math.max(0, localTop - tipPad);
    const placeAbove = spaceBelow < tipHeightEst + tipMargin && spaceAbove > spaceBelow;
    const localTooltipTop = placeAbove
      ? Math.max(tipPad, localTop - tipHeightEst - tipMargin)
      : localBottom + tipMargin;

    let localTooltipLeft = localCenterX - tipWidth / 2;
    const maxLeft = canvasRect.width - tipWidth - tipPad;
    if (localTooltipLeft > maxLeft) localTooltipLeft = maxLeft;
    if (localTooltipLeft < tipPad) localTooltipLeft = tipPad;

    const modeLabel = this.props.gameMode === 'peasant' ? 'Peasant' : 'Constructed';

    return (
      <div
        role="tooltip"
        style={{
          position: 'fixed',
          left: localTooltipLeft,
          top: localTooltipTop,
          width: `${tipWidth}px`,
          pointerEvents: 'none',
          zIndex: 90,
          padding: '0.5cqw 0.75cqw',
          background: 'linear-gradient(180deg, rgba(28,8,8,0.98) 0%, rgba(20,4,4,0.98) 100%)',
          border: '1px solid rgba(220, 80, 80, 0.55)',
          borderRadius: 'var(--radius-md)',
          boxShadow: '0 0.5cqw 1.25cqw rgba(0,0,0,0.55), 0 0 1.25cqw rgba(220,80,80,0.18), inset 0 1px 0 rgba(255, 140, 140, 0.08)',
        }}
      >
        <div
          className="text-[0.625cqw] font-semibold"
          style={{ color: '#ff9090' }}
        >
          Not legal in {modeLabel}
        </div>
        <div
          className="text-[0.6875cqw] mt-1 leading-snug"
          style={{ color: TEXT_MUTED }}
        >
          You own {owned} of {need} needed.
        </div>
      </div>
    );
  }

  renderTypeSubHeader(zoneKey, type, count, collapsed) {
    const headerId = `type:${zoneKey}:${type}`;
    const isHovered = this.state.hoveredHeader === headerId;
    const isPressed = this.state.pressedHeader === headerId;
    let bg = 'none';
    if (isPressed) bg = `${GOLD} 0.12)`;
    else if (isHovered) bg = `${GOLD} 0.05)`;
    return (
      <button
        key={`subheader-${zoneKey}-${type}`}
        type="button"
        className="w-full flex items-center gap-1.5 px-2 py-0.5 cursor-pointer"
        style={{
          background: bg,
          border: 'none',
          borderRadius: 'var(--radius-sm)',
          marginLeft: 6,
          transform: isPressed ? 'scale(0.98)' : 'none',
          transitionProperty: 'background-color, transform',
          transitionDuration: '150ms, 100ms',
          transitionTimingFunction: 'ease',
        }}
        onClick={() => this.toggleTypeSection(zoneKey, type)}
        onMouseEnter={() => this.setHeaderHover(headerId, true)}
        onMouseLeave={() => { this.setHeaderHover(headerId, false); this.setHeaderPressed(headerId, false); }}
        onMouseDown={() => this.setHeaderPressed(headerId, true)}
        onMouseUp={() => this.setHeaderPressed(headerId, false)}
      >
        <span
          className="text-[0.5625cqw] transition-transform duration-150"
          style={{
            color: `${GOLD} 0.35)`,
            transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
            display: 'inline-block',
          }}
        >
          &#9660;
        </span>
        <span
          className="text-[0.625cqw] font-medium"
          style={{ color: TEXT_MUTED }}
        >
          {TYPE_LABELS[type] || type}
        </span>
        <span className="text-[0.625cqw] ml-auto" style={{ color: `${GOLD} 0.3)` }}>
          ({count})
        </span>
      </button>
    );
  }

  render() {
    const { chosenCards = [] } = this.props;
    const { collapsed } = this.state;

    const grouped = { avatar: [], spellbook: [], atlas: [], collection: [] };
    for (const entry of chosenCards) {
      // Trust the per-entry zone when set. Imposter decks intentionally
      // park additional avatars in the spellbook or collection, and
      // collapsing every Avatar-type entry into the avatar bucket would
      // hide them from the user. Only fall back to a type-based default
      // when the entry has no zone at all (legacy hydrate path).
      let zone;
      if (entry.zone && grouped[entry.zone]) {
        zone = entry.zone;
      } else if (entry.card?.type === 'Avatar' || entry.card?._sorceryCategory === 'Avatar') {
        zone = 'avatar';
      } else {
        const cat = entry.card?._sorceryCategory?.toLowerCase();
        if (cat === 'site' || entry.card?.type === 'Site') zone = 'atlas';
        else if (entry.isSideboard) zone = 'collection';
        else zone = 'spellbook';
      }
      grouped[zone].push(entry);
    }

    const totalCount = chosenCards.length;

    return (
      <div
        className="flex flex-col h-full select-none"
        style={{
          background: PANEL_BG,
          // Layered shadow instead of a flat 2px rule: an outward cast onto
          // the collection grid, a dark seam, then a warm 1px lip. The panel
          // reads as a slab standing proud of the page rather than a line.
          boxShadow: [
            '-0.5cqw 0 1.25cqw rgba(0,0,0,0.45)',
            'inset 1px 0 0 rgba(0,0,0,0.6)',
            `inset 2px 0 0 ${GOLD} 0.28)`,
          ].join(', '),
        }}
      >
        {/* Header */}
        <div className="px-3 pt-3 pb-1 flex items-baseline justify-between">
          <span
            className="text-sm font-bold"
            style={{ color: TEXT_PRIMARY }}
          >
            Deck
          </span>
          <span className="text-xs" style={{ color: TEXT_MUTED }}>
            {totalCount} Card{totalCount !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Sort selector. Toggles the within-type order between name and cost. */}
        <div className="px-3 pt-1 pb-1 flex items-center gap-0.5" aria-label={t('Sort deck cards by')}>
          <span
            className="text-[0.625cqw] mr-1"
            style={{ color: `${GOLD} 0.45)` }}
          >
            Sort
          </span>
          {CARD_SORT_OPTIONS.map((opt) => {
            const active = this.state.sortBy === opt.value;
            const isCost = opt.value === CARD_SORT_BY_COST;
            // Arrow slot always rendered (faded when inactive) so the chip
            // keeps one width and its neighbors never shift.
            return (
              <button
                key={opt.value}
                type="button"
                className="filter-chip transition-[background-color,border-color,color,transform] duration-100 active:scale-[0.96]"
                data-active={active ? 'true' : 'false'}
                aria-label={
                  active && isCost
                    ? `Sorted by Cost ${this.state.sortDir === CARD_SORT_DIR_DESC ? 'descending' : 'ascending'}; click to reverse`
                    : `Sort by ${opt.label}`
                }
                aria-pressed={active}
                onClick={() => this.handleSortByChange(opt.value)}
              >
                {opt.label}
                {isCost ? (
                  <span
                    aria-hidden="true"
                    className="inline-block transition-opacity duration-100"
                    style={{ marginLeft: '0.2cqw', opacity: active ? 1 : 0 }}
                  >
                    {this.state.sortDir === CARD_SORT_DIR_DESC ? '↓' : '↑'}
                  </span>
                ) : null}
              </button>
            );
          })}
          {/* Artwork toggle. Washes each row with the card's art. */}
          <button
            type="button"
            className="filter-chip ml-auto inline-flex items-center gap-1 transition-[background-color,border-color,color,transform] duration-100 active:scale-[0.96]"
            data-active={this.state.showArt ? 'true' : 'false'}
            aria-label={this.state.showArt ? 'Hide card artwork' : 'Show card artwork'}
            aria-pressed={this.state.showArt}
            title={this.state.showArt ? 'Hide card artwork' : 'Show card artwork'}
            onClick={this.toggleArt}
          >
            <ImageIcon style={{ width: '0.75cqw', height: '0.75cqw' }} />
            Art
          </button>
        </div>

        <OrnamentalDivider className="px-3 py-1" />

        {/* Scrollable sections */}
        {/* Bottom-only fade: the last rows dissolve into the panel edge so
            a scrolled list reads as continuing rather than being cut off.
            No top fade. It would eat the sticky zone headers. */}
        <div
          className="flex-1 overflow-y-auto overflow-x-hidden px-2 pb-2 medieval-scrollbar"
          style={{
            maskImage: 'linear-gradient(to bottom, #000 calc(100% - 1cqw), transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, #000 calc(100% - 1cqw), transparent 100%)',
          }}
        >
          {(() => {
            // Filter ZONES based on the currently-selected avatar's
            // deckbuilding rules. Magician's forbidAtlas collapses the
            // Atlas panel entirely. Its sites live in the Spellbook
            // zone instead, so showing an empty Atlas section would be
            // actively misleading.
            // Pick the primary avatar (the one in the avatar slot), not
            // just the first Avatar-typed entry. Imposter decks can
            // park other avatars in the spellbook/collection and those
            // must not drive the zone-visibility rules.
            const cards = this.props.chosenCards;
            const avatar =
              cards?.find((e) => e.card?.type === 'Avatar' && e.zone === 'avatar')?.card
              || cards?.find((e) => e.card?.type === 'Avatar' && !e.zone)?.card
              || null;
            const rules = avatar ? resolveAvatarRules(avatar).deckbuilding : null;
            const visibleZones = rules?.forbidAtlas
              ? ZONES.filter((z) => z.key !== 'atlas')
              : ZONES;
            return visibleZones;
          })().map(({ key, label }) => {
            const entries = grouped[key];
            const rows = entries.length ? aggregateAndSort(entries, this.state.sortBy, this.state.sortDir) : [];
            const isCollapsed = collapsed[key];

            const isDragOver = this.state.dragOverZone === key;
            const canDrop = this.state.dragCardId && this.canMoveToZone(this.state.dragSourceZone, key, this.state.dragIsSite);

            return (
              <div
                key={key}
                className="mb-1 transition-all duration-150"
                style={{
                  borderRadius: 'var(--radius-md)',
                  background: isDragOver ? `${GOLD} 0.06)` : 'transparent',
                  outline: isDragOver ? `1.5px dashed ${GOLD} 0.35)` : 'none',
                  outlineOffset: '-1px',
                }}
                onDragOver={this.handleDragOver}
                onDragEnter={() => this.handleSectionDragEnter(key)}
                onDragLeave={(e) => this.handleSectionDragLeave(e, key)}
                onDrop={(e) => this.handleDrop(e, key)}
              >
                {/* Section header. Drop target */}
                {(() => {
                  const headerId = `zone:${key}`;
                  const isHovered = this.state.hoveredHeader === headerId;
                  const isPressed = this.state.pressedHeader === headerId;
                  // Solid, not a gold wash. The header pins to the top of the
                  // scroll container, so rows sliding underneath must not
                  // show through it.
                  let headerBg = '#0e0b08';
                  if (isDragOver) headerBg = '#1e1608';
                  else if (isPressed) headerBg = '#231a0b';
                  else if (isHovered) headerBg = '#171108';
                  return (
                <button
                  type="button"
                  className="sticky top-0 z-[2] w-full flex items-center gap-1.5 px-2 py-1.5 cursor-pointer"
                  style={{
                    background: headerBg,
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    // Cast onto the rows below so the pinned header reads as
                    // the nearer surface.
                    boxShadow: '0 0.15cqw 0.4cqw rgba(0,0,0,0.5)',
                    transform: isPressed ? 'scale(0.98)' : 'none',
                    transitionProperty: 'background-color, transform',
                    transitionDuration: '150ms, 100ms',
                    transitionTimingFunction: 'ease',
                  }}
                  onClick={() => this.toggleSection(key)}
                  onMouseEnter={() => this.setHeaderHover(headerId, true)}
                  onMouseLeave={() => { this.setHeaderHover(headerId, false); this.setHeaderPressed(headerId, false); }}
                  onMouseDown={() => this.setHeaderPressed(headerId, true)}
                  onMouseUp={() => this.setHeaderPressed(headerId, false)}
                >
                  <span
                    className="text-[0.625cqw] transition-transform duration-150"
                    style={{
                      color: isDragOver ? `${GOLD} 0.8)` : `${GOLD} 0.45)`,
                      transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                      display: 'inline-block',
                    }}
                  >
                    &#9660;
                  </span>
                  <span
                    className="text-xs font-semibold"
                    style={isDragOver ? { color: `${GOLD} 0.8)`, textShadow: `0 0 10px ${GOLD} 0.2)` } : SECTION_HEADER_STYLE}
                  >
                    {label}
                  </span>
                  <span
                    className="text-[0.625cqw] ml-auto"
                    style={{ color: isDragOver ? `${GOLD} 0.6)` : `${GOLD} 0.35)` }}
                  >
                    ({entries.length})
                  </span>
                </button>
                  );
                })()}

                {/* Card rows, optionally grouped under collapsible type sub-headings.
                    AnimatePresence gives removed rows a quick fade-out while the
                    surviving rows' layout animation closes the gap. Covers
                    remove-from-deck and zone moves (exit here, enter there). */}
                <AnimatePresence initial={false} mode="popLayout">
                  {!isCollapsed ? (() => {
                    const typeGroups = groupRowsByType(rows);
                    const showSubHeadings = typeGroups.length > 1;
                    return typeGroups.flatMap(({ type, rows: typeRows }) => {
                      const typeKey = `${key}:${type}`;
                      const typeCollapsed = this.state.collapsedTypes.has(typeKey);
                      const out = [];
                      if (showSubHeadings) {
                        const typeCount = typeRows.reduce((sum, r) => sum + r.qty, 0);
                        out.push(this.renderTypeSubHeader(key, type, typeCount, typeCollapsed));
                      }
                      if (!typeCollapsed) {
                        for (const row of typeRows) out.push(this.renderCardRow(row, key));
                      }
                      return out;
                    });
                  })() : null}
                </AnimatePresence>

                {/* Ghost preview row. Shows where the dragged card will land */}
                {isDragOver && canDrop && this.state.dragCardName && (
                  <div
                    className="flex items-center gap-1.5 px-2 rounded mx-1"
                    style={{
                      height: '2cqw',
                      background: `${GOLD} 0.08)`,
                      border: `1px dashed ${GOLD} 0.3)`,
                      borderRadius: 'var(--radius-sm)',
                      marginTop: 2,
                      marginBottom: 2,
                    }}
                  >
                    <span className="text-xs font-medium shrink-0" style={{ color: `${GOLD} 0.5)`, width: '1.25cqw', textAlign: 'right' }}>
                      +
                    </span>
                    <span className="text-xs truncate flex-1" style={{ color: `${GOLD} 0.6)`, fontStyle: 'italic' }}>
                      {this.state.dragCardName}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Floating warning for cards illegal in the deck's format
            (currently only triggered by peasant). Anchored to the
            left of the hovered row in viewport space so it can never
            be clipped by the sidebar's scroll container, and made
            pointer-transparent so it doesn't intercept the hover
            that produced it. */}
        {this.state.illegalHover ? this.renderIllegalHoverTooltip() : null}
        {/* Floating warning for cards that violate a structural deck
            rule (currently only Imposter extras when the primary
            avatar isn't Imposter). Same positioning math as the
            peasant tooltip, different theme + copy. */}
        {this.state.ruleInvalidHover ? this.renderRuleInvalidHoverTooltip() : null}
        {/* Floating warning for unowned cards in ranked-eligible modes
            (constructed / peasant). Explains why the row is tinted red
            when there's no peasant or rule violation to fault. */}
        {this.state.unownedHover ? this.renderUnownedHoverTooltip() : null}

        {/* Context menu for moving cards between zones */}
        <ContextMenuContent
          ariaLabel="Card zone actions"
          open={!!this.state.contextMenu}
          position={this.state.contextMenu}
          onOpenChange={(open) => { if (!open) this.closeContextMenu(); }}
        >
          {this.state.contextMenu && (() => {
            const { currentZone, isSite } = this.state.contextMenu;
            return (
              <>
                {this.canMoveToZone(currentZone, 'spellbook', isSite) && (
                  <ContextMenuItem onClick={() => this.handleMoveToZone('spellbook')}>
                    Move to Spellbook
                  </ContextMenuItem>
                )}
                {this.canMoveToZone(currentZone, 'atlas', isSite) && (
                  <ContextMenuItem onClick={() => this.handleMoveToZone('atlas')}>
                    Move to Atlas
                  </ContextMenuItem>
                )}
                {this.canMoveToZone(currentZone, 'collection', isSite) && (
                  <ContextMenuItem onClick={() => this.handleMoveToZone('collection')}>
                    Move to Collection
                  </ContextMenuItem>
                )}
                <ContextMenuSeparator />
                <ContextMenuItem onClick={this.handleRemoveFromMenu}>
                  Remove from Deck
                </ContextMenuItem>
              </>
            );
          })()}
        </ContextMenuContent>
      </div>
    );
  }
}
